import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { chatsApi } from '../lib/chatsApi';
import { r16pUiEnabled } from '../lib/r14Switch';
import { classifyChatsError, normaliseSummary, chatLog } from '../lib/chatsView';
import { chatsKeys, keepPreviousForOwner } from '../lib/chatsKeys';
import useChatsOwner from './useChatsOwner';

/*
 * r16p (design 6.11 / 7.2): the account's chat counts - GET /uiapi/chats/summary, every 60 s while the tab is visible (TanStack stops
 * interval polls in a hidden tab). ONE query key shared by the nav pill, the Slots page's Inbox link and the Inbox page's counts
 * strip, so mounting all three costs one request a minute. The key carries the signed-in account (review T1, lib/chatsKeys.js):
 * another account in the same tab never sees these counts.
 *
 * `available` is the gate for every r16p entry point (nav item, Slots link): true once the route answered 200. A server before r16p
 * (404 with no code of ours) or with chats switched off (not_enabled) leaves it false - exactly r16n - and logs one line. So does a
 * 401 / 403 (a login type with no chat access, e.g. an Inventory client login, or a session older than "Lock API access"): those
 * calls skip lib/api.js's global /login redirect (necroNoAuthRedirect), so a poll on every page can never bounce the dashboard.
 */

let loggedMissing = false;

export default function useChatsSummary({ enabled = true } = {}) {
  const on = r16pUiEnabled() && enabled;
  const owner = useChatsOwner();
  const q = useQuery({
    queryKey: chatsKeys.summary(owner),
    queryFn: () => chatsApi.summary().then((r) => r.data),
    enabled: on,
    retry: false,
    refetchInterval: 60000,
    staleTime: 30000,
    placeholderData: keepPreviousForOwner(owner),
  });
  const err = q.error ? classifyChatsError(q.error) : null;
  useEffect(() => {
    if (err && !loggedMissing) {
      loggedMissing = true;
      chatLog('info', 'summary unavailable', { kind: err.kind, status: err.status, code: err.code });
    }
  }, [err]);
  const summary = useMemo(() => (q.data ? normaliseSummary(q.data) : null), [q.data]);
  // once answered, a passing error (a busy 503, a network blip) keeps the last counts on screen; only "no route" / "switched off"
  // takes the entry points away again
  const gone = !!err && (err.kind === 'routeMissing' || err.kind === 'notEnabled' || err.kind === 'sessionRequired' || err.kind === 'noAccess');
  const available = on && !!q.data && !gone;
  /*
   * r16pm review (2026-10-25): the three API-key LEVERS (list keys, revoke a key, Lock API access) stay reachable on the server for a
   * login with no chat access (403 no_chat_access) and while the server's chats switch is off (404 chats_disabled) - on purpose, so
   * a key can always be revoked (SERVER-CONTRACT.md 1). `levers` keeps the API access entry point (the Settings card) in those two
   * cases, while the Inbox / Chat data entry points stay gone. A server before r16p (a 404 with no code), a session signed out of the
   * chat pages (401) or any other error: false, as before.
   */
  const levers = available || (on && !!err && (err.code === 'no_chat_access' || err.code === 'chats_disabled'));
  return {
    on,
    available,
    levers,
    summary,
    error: err,
    isLoading: on && q.isLoading,
    refetch: q.refetch,
    owner,
  };
}
